// @flow

import * as React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';

import { createSelector } from 'reselect';

import { selectPricingInfos } from '@gandi/billing';

import { useResourceFetchOne } from '@gandi/resource/react';

import type { FetchParams } from '@gandi/resource/redux';

import { getDataDependency, getDisplayName } from 'gandi.react-helpers';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { accountsResource } from '../../modules/cloud/resources/accounts';
import type { Account } from '../../modules/cloud/resources/accounts';
import type { FullState, PreFetchArgs, PreFetchReturn } from '../../types';

export type FetchAccountParams = {
  country: string,
  currency: string,
  duration: number,
  duration_unit: 'd' | 'm' | 'y' | 'h',
  grid: string,
  headers?: { ... },
  params?: { [string]: mixed, ... },
  sharing_id: string,
};

export const selectFetchAccountParams: (
  state: FullState,
) => FetchAccountParams = createSelector(
  [selectPricingInfos],
  pricingInfos => ({
    country: pricingInfos.country,
    currency: pricingInfos.currency,
    duration_unit: 'd',
    duration: 30,
    grid: pricingInfos.grid,
    sharing_id: pricingInfos.sharingId,
  }),
);

export function useAccount(): {
  account: ?Account,
  fetchAccountParams: FetchAccountParams,
  isFailureAccount: boolean,
  isFetchingAccount: boolean,
  isForbiddenAccount: boolean,
  isLoadedAccount: boolean,
  isNotFoundAccount: boolean,
  label: string,
  shouldFetchAccount: boolean,
} {
  const fetchAccountParams = useAppSelector(selectFetchAccountParams);
  const {
    detail: account,
    isFailure: isFailureAccount,
    isFetching: isFetchingAccount,
    isForbidden: isForbiddenAccount,
    isLoaded: isLoadedAccount,
    isNotFound: isNotFoundAccount,
    label,
    shouldFetch: shouldFetchAccount,
  } = useResourceFetchOne(
    accountsResource,
    // $FlowFixMe[incompatible-cast]
    (fetchAccountParams: FetchParams),
  );

  return {
    account,
    fetchAccountParams,
    isFailureAccount,
    isFetchingAccount,
    isForbiddenAccount,
    isLoadedAccount,
    isNotFoundAccount,
    label,
    shouldFetchAccount,
  };
}

export function preFetchAccount(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  const fetchParams = selectFetchAccountParams(args.getState());
  const label = accountsResource.computeLabel(
    accountsResource.options.fetchOnePath,
    // $FlowFixMe[incompatible-cast]
    (fetchParams: FetchParams),
  );
  return args.dispatch(
    accountsResource.fetchOne(accountsResource.options.fetchOnePath, {
      label,
      // $FlowFixMe[incompatible-call]
      params: fetchParams,
    }),
  );
}

export function withAccount<C: React.ComponentType<any>>(
  WrappedComponent: C,
): any {
  function WithAccount(props: React.ElementConfig<C>) {
    const accountProps = useAccount();

    return <WrappedComponent {...props} {...accountProps} />;
  }

  const HoistedComponent = hoistNonReactStatics(WithAccount, WrappedComponent);

  HoistedComponent.WrappedComponent = WrappedComponent;
  HoistedComponent.displayName = `WithAccount(${getDisplayName(
    WrappedComponent,
  )})`;
  HoistedComponent.preFetch = getDataDependency(WrappedComponent, 'preFetch');

  return HoistedComponent;
}
